import { ChangeEvent, VFC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { getTokensToDisplayValue, UsableObjectConfig } from 'dashboard/lib/workflows';
import { getAttributesToCompare } from 'dashboard/models/verification-check';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  WorkflowActionCreateVerificationGovernmentIdFromInquiryV1,
  WorkflowActionCreateVerificationSelfieFromInquiryV1,
  WorkflowRunVerificationAction,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';

export interface ParamsCreateVerificationCustomProps {
  action: WorkflowRunVerificationAction;
  editable: boolean;
  errors: WorkflowError[];
  updateActionParams: (newParams: WorkflowRunVerificationAction['params']) => void;
  usableObjects: UsableObjectConfig[];
  verificationTemplateVersion: VerificationTemplateVersion | undefined;
}

function getAttributesToCompareMapping(
  params:
    | WorkflowActionCreateVerificationGovernmentIdFromInquiryV1['params']
    | WorkflowActionCreateVerificationSelfieFromInquiryV1['params'],
  attributesToCompare: Set<string>,
): Record<string, string> {
  const existingMapping = params['comparison-attribute-to-inquiry-field-map'];
  if (existingMapping && Object.keys(existingMapping).length > 0) {
    return existingMapping;
  } else {
    return Object.fromEntries(
      Array.from(attributesToCompare).map((attribute) => [
        attribute,
        attribute === 'selfies' ? 'trigger.fields.current_selfie' : `trigger.fields.${attribute}`,
      ]),
    );
  }
}

const ParamsCreateVerificationCustom: VFC<ParamsCreateVerificationCustomProps> = ({
  action,
  editable,
  errors,
  updateActionParams,
  usableObjects,
  verificationTemplateVersion,
}: ParamsCreateVerificationCustomProps) => {
  const { key, params } = action;
  const [showEditPanel, { toggle: toggleEditPanel, disable: closeEditPanel }] = useToggle(false);

  if (!verificationTemplateVersion) {
    return null;
  }

  // Enables editing the comparison attribute to inquiry field mapping for the dynamic flow
  // inquiry-created verification case. For the run_verification_v1 editor equivalent, see
  // front-end/dashboard/components/WorkflowControlPanel/StepConfigAction/RunVerificationConfigMapping.tsx
  if (
    key === WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry ||
    key === WorkflowStepActionKey.CreateVerificationSelfieFromInquiry
  ) {
    const comparisonAttributes = getAttributesToCompare(verificationTemplateVersion);
    if (comparisonAttributes.size > 0) {
      const comparisonAttributeMapping = getAttributesToCompareMapping(
        params,
        comparisonAttributes,
      );

      const editComparisonAttributes = (comparisonAttributes: Record<string, string>) => {
        updateActionParams({
          ...params,
          'comparison-attribute-to-inquiry-field-map': comparisonAttributes,
        });
      };

      return (
        <>
          <Spacer height={4} />
          <Accordion title='Advanced Configuration'>
            {showEditPanel && (
              <FieldMappingsPanel
                title='Comparison Attributes'
                usableObjects={usableObjects}
                fieldMappings={comparisonAttributeMapping}
                onOutsideClick={closeEditPanel}
                onUpdateFieldMappings={editComparisonAttributes}
                removable={false}
              />
            )}
            <Spacer height={1} />
            <FieldsList
              fields={comparisonAttributeMapping}
              fieldKey={`${verificationTemplateVersion.id}.comparison-attributes`}
              editable={editable}
              toggleEditPanel={toggleEditPanel}
              tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
              errors={errors}
            />
          </Accordion>
        </>
      );
    }
  } else if (key === WorkflowStepActionKey.RunVerificationDatabaseBusiness) {
    const handleCompleteOnTentativeChange = (value: boolean) => {
      updateActionParams({
        ...params,
        'complete-on-tentative': value,
      });
    };

    return (
      <>
        <Spacer height={4} />
        <Checkbox
          label={<p>Complete on tentative?</p>}
          disabled={!editable}
          checked={params['complete-on-tentative']}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleCompleteOnTentativeChange(e.target.checked);
          }}
        />
      </>
    );
  }

  return null;
};

export default ParamsCreateVerificationCustom;
